import { GetStaticPaths, GetStaticProps } from 'next';

import {
  PrismicAllProjectsQueryDocument,
  PrismicProjectQueryDocument,
  PrismicProjectPathsDocument,
} from '@prismic/gqlTypes/prismicHooks';
import {
  PrismicAllProjectsQuery,
  PrismicAllProjectsQueryVariables,
  PrismicProjectQuery,
  PrismicProjectQueryVariables,
  PrismicProjectPathsQuery,
  PrismicProjectPathsQueryVariables,
  ProjectCategoryFragment,
} from '@prismic/gqlTypes/prismicOperations';

import {
  incrementalStaticRegenerationRevalidate,
  staticPathsFallback,
} from '@temp/constants';

import { prismicApolloClient } from '@utils/ssg';
import { localeToPrismicQuery, prismicToLocale } from '@utils/regions';

import { generatePrismicSeo } from '@utils/seo';
import { paths } from '@temp/paths';

import { ProjectPage, ProjectPageProps } from '@temp/views/Project';

export default ProjectPage;

export const getStaticPaths: GetStaticPaths<any> = async ({ locales }) => {
  const paths = [];

  await Promise.all(
    locales.map(async (locale) => {
      const { data } = await prismicApolloClient.query<
        PrismicProjectPathsQuery,
        PrismicProjectPathsQueryVariables
      >({
        query: PrismicProjectPathsDocument,
        variables: { ...localeToPrismicQuery(locale) },
      });

      const lpaths = data.allProjects.edges.map(({ node }) => ({
        params: { slug: node._meta.uid },
        locale: prismicToLocale(node._meta.lang),
      }));

      paths.push(...lpaths);
    })
  );

  return { paths, fallback: staticPathsFallback };
};

export const getStaticProps: GetStaticProps<
  ProjectPageProps,
  ProjectPageProps['params']
> = async ({ params: { slug }, locale, defaultLocale }) => {
  const { data: projectSSG } = await prismicApolloClient.query<
    PrismicProjectQuery,
    PrismicProjectQueryVariables
  >({
    query: PrismicProjectQueryDocument,
    variables: {
      uid: slug,
      ...localeToPrismicQuery(locale),
    },
  });

  if (!projectSSG?.project) {
    return {
      notFound: true,
    };
  }

  const { data: allProjectsSSG } = await prismicApolloClient.query<
    PrismicAllProjectsQuery,
    PrismicAllProjectsQueryVariables
  >({
    query: PrismicAllProjectsQueryDocument,
    variables: {
      ...localeToPrismicQuery(locale),
    },
  });

  const seo = await generatePrismicSeo(
    projectSSG.project,
    paths.inspirationsPost,
    'article',
    locale,
    defaultLocale
  );

  const project = !projectSSG
    ? null
    : {
        category: (projectSSG.project.category_list || [])
          .map((x) => (x?.category as ProjectCategoryFragment)?.name || '')
          .filter((x) => Boolean(x))
          .join(`, `),
        color:
          projectSSG.project.color &&
          'name' in projectSSG.project.color &&
          projectSSG.project.color.name,
        description: projectSSG.project.description,
        door_size: projectSSG.project.door_size,
        glass_type: projectSSG.project.glass_type,
        handle_color: projectSSG.project.handle_color,
        handle_style: projectSSG.project.handle_style,
        height: projectSSG.project.height,
        interior_designer_link: projectSSG.project.interior_designer_link,
        interior_designer_view_name:
          projectSSG.project.interior_designer_view_name,
        product_link: projectSSG.project.product_link,
        product_view_name: projectSSG.project.product_view_name,
        media: projectSSG.project.media,
        tag_list: projectSSG.project.tag_list
          .filter((z) => z && z.tag_item)
          .map((z) => z.tag_item),
        title: projectSSG.project.title,
        width:
          projectSSG.project.width &&
          'width' in projectSSG.project.width &&
          projectSSG.project.width.width,
      };

  const slices = projectSSG.project?.body || [];

  const projects = !allProjectsSSG
    ? []
    : allProjectsSSG.allProjects.edges.map((z) => ({
        category: (z.node?.category_list || [])
          .map((x) => (x?.category as ProjectCategoryFragment)?.name || '')
          .filter((x) => Boolean(x))
          .join(`, `),
        id: z.node._meta.id,
        image: z.node.image,
        name: z.node._meta.uid,
        title: z.node.title,
        _meta: z.node._meta,
      }));

  return {
    revalidate: incrementalStaticRegenerationRevalidate,
    props: {
      project,
      projects,
      slices,
      seo,
      params: { slug },
    },
  };
};
